'use client';

import React from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Typography,
  Tooltip,
  type SelectChangeEvent,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { VtkDatasetInfo } from '../types/vtk';

export const FIELD_OPTIONS = [
  {
    value: 'control_velocity_magnitude',
    label: 'Velocity Magnitude',
  },
  {
    value: 'adjoint_velocity_magnitude',
    label: 'Adjoint Velocity Magnitude',
  },
  { value: 'control_velocity', label: 'Velocity (Streamlines)' },
  { value: 'adjoint_velocity', label: 'Adjoint Velocity (Streamlines)' },
  { value: 'control_pressure', label: 'Pressure' },
  { value: 'adjoint_pressure', label: 'Adjoint Pressure' },
] as const;

export type ViewFieldKey = (typeof FIELD_OPTIONS)[number]['value'];

type ControlPanelProps = {
  dataset: VtkDatasetInfo;
  reValue: number;
  field: ViewFieldKey;
  loadingMesh: boolean;
  loadingStreamlines: boolean;
  onReChange: (_: Event, value: number | number[]) => void;
  onReChangeCommitted: (
    _: Event,
    value: number | number[],
  ) => void | Promise<void>;
  onFieldChange: (
    event: SelectChangeEvent<ViewFieldKey>,
  ) => void | Promise<void>;
  onResetCamera: () => void;
};

export default function ControlPanel({
  dataset,
  reValue,
  field,
  loadingMesh,
  loadingStreamlines,
  onReChange,
  onReChangeCommitted,
  onFieldChange,
  onResetCamera,
}: ControlPanelProps) {
  const fieldLabel =
    FIELD_OPTIONS.find((option) => option.value === field)?.label ?? field;

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        boxShadow: 2,
        borderRadius: 2,
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      <Typography variant='h5' gutterBottom>
        Optimal Flow Control in a Bypass Model
      </Typography>

      {/**
      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 3,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
        }}
      >
      */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            lineHeight: 1.35,
            fontSize: '0.88rem',
            textAlign: 'justify',
          }}
        >
          This application simulates blood flow dynamics within a
          patient-specific coronary bypass, with a focus on how varying blood
          properties influence the surgical outcome. The tool leverages advanced
          optimization techniques within an optimal control framework to ensure
          consistency with real-world clinical data. The interface allows
          real-time exploration of bypass performance under different
          physiological conditions, such as changes in blood viscosity and
          fluidity. Key hemodynamic variables, including velocity and pressure,
          can be visualized to assess how the model adheres to the
          patient-specific physiological behavior.{' '}
        </Typography>
      </Box>
      {/* </Box> */}

      <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 1,
            }}
          >
            <Typography variant='h6' sx={{ mb: 0 }}>
              Reynolds Number: {reValue.toFixed(2)}
            </Typography>

            <Tooltip
              title='The Reynolds number describes the flow regime of a fluid. Higher values indicate less viscous and more turbulent flow.'
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.secondary',
                  cursor: 'help',
                }}
              >
                <InfoOutlinedIcon fontSize='small' />
              </Box>
            </Tooltip>
          </Box>

          <Slider
            value={reValue}
            min={70}
            max={80}
            step={0.01}
            valueLabelDisplay='auto'
            onChange={onReChange}
            onChangeCommitted={onReChangeCommitted}
          />
        </Box>

        <FormControl fullWidth>
          <InputLabel id='field-select-label' sx={{ fontSize: '1.0rem' }}>
            Field
          </InputLabel>
          <Select
            labelId='field-select-label'
            sx={{ fontSize: '1.0rem' }}
            value={field}
            label='Field'
            onChange={onFieldChange}
          >
            {FIELD_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant='outlined' size='medium' onClick={onResetCamera}>
          Reset Camera
        </Button>
      </Box>
      {/*
      <Typography variant='body1' sx={{ mb: 1 }}>
        <strong>Number of Points:</strong> {dataset.metadata.numberOfPoints}
      </Typography>

      <Typography variant='body1' sx={{ mb: 1 }}>
        <strong>Number of Cells:</strong> {dataset.metadata.numberOfCells}
      </Typography>

      <Typography variant='body1' sx={{ mb: 1 }}>
        <strong>Current Re:</strong> {reValue.toFixed(2)}
      </Typography>

      <Typography variant='body1' sx={{ mb: 1 }}>
        <strong>Selected field:</strong> {fieldLabel}
      </Typography>
    */}
      <Typography variant='body2' sx={{ mt: 2, color: 'text.secondary' }}>
        Mesh loading: {loadingMesh ? 'running' : 'idle'}
        <br />
        Streamline loading: {loadingStreamlines ? 'running' : 'idle'}
      </Typography>

      <Box
        sx={{
          mt: 3,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant='caption'
          sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}
        >
          References
        </Typography>
        <Typography
          variant='caption'
          component='div'
          sx={{ color: 'text.secondary', lineHeight: 1.5, fontSize: '0.7rem' }}
        >
          [1] Balzotti, C., Siena, P., Girfoglio, M., Quaini, A., & Rozza, G.
          (2022). A data-driven reduced order method for parametric optimal blood
          flow control: application to coronary bypass graft.{' '}
          <i>arXiv preprint arXiv:2206.15384</i>.
          <br />
          [2] Zainib Z, Ballarin F, Fremes S, Triverio P, Jiménez-Juan L, Rozza G.
          Reduced order methods for parametric optimal flow control in coronary
          bypass grafts, toward patient-specific data assimilation.{' '}
          <i>Int J Numer Meth Biomed Engng.</i> 2021; 37:e3367.{' '}
          https://doi.org/10.1002/cnm.3367
        </Typography>
      </Box>
    </Box>
  );
}
